import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Webhook, Send, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WebhookSettings = ({ qrId, onClose }) => {
  const { token } = useAuth();
  const [webhookUrl, setWebhookUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWebhook();
  }, [qrId]);

  const fetchWebhook = async () => {
    try {
      const res = await axios.get(`${API}/qr-codes/${qrId}/webhook`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWebhookUrl(res.data.webhook_url || '');
    } catch {
      // No webhook configured
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/qr-codes/${qrId}/webhook`,
        { webhook_url: webhookUrl || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Webhook saved');
      setTestResult(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save webhook');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!webhookUrl) {
      toast.error('Save a webhook URL first');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(
        `${API}/webhooks/test`,
        { qr_id: qrId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResult(res.data);
      if (res.data.success) {
        toast.success('Webhook test successful');
      } else {
        toast.error(`Webhook test failed: ${res.data.response_body}`);
      }
    } catch (err) {
      toast.error('Failed to test webhook');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-[#1d4ed8]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="webhook-settings">
      <div className="flex items-center gap-2 mb-2">
        <Webhook className="h-5 w-5 text-[#1d4ed8]" />
        <h3 className="font-bold text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Webhook Configuration
        </h3>
      </div>

      <p className="text-sm text-[#475569]">
        Receive real-time notifications when forms are submitted. We'll POST a JSON payload to your URL.
      </p>

      <div>
        <Label htmlFor="webhook-url">Webhook URL</Label>
        <Input
          id="webhook-url"
          data-testid="webhook-url-input"
          type="url"
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
          placeholder="https://your-server.com/webhook"
          className="mt-1"
        />
      </div>

      <div className="flex gap-2">
        <Button
          data-testid="webhook-save-btn"
          onClick={handleSave}
          disabled={saving}
          className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]"
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
        <Button
          data-testid="webhook-test-btn"
          onClick={handleTest}
          disabled={testing || !webhookUrl}
          variant="outline"
          className="rounded-full"
        >
          {testing ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          Test Webhook
        </Button>
      </div>

      {testResult && (
        <div
          data-testid="webhook-test-result"
          className={`p-3 rounded-lg border text-sm ${
            testResult.success
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            {testResult.success ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <XCircle className="h-4 w-4" />
            )}
            <span className="font-medium">
              {testResult.success ? 'Success' : 'Failed'} — HTTP {testResult.status_code}
            </span>
          </div>
          {testResult.response_body && (
            <pre className="text-xs mt-1 whitespace-pre-wrap break-all opacity-80">
              {testResult.response_body.slice(0, 200)}
            </pre>
          )}
        </div>
      )}

      <div className="pt-2 border-t border-[#E2E8F0]">
        <p className="text-xs text-[#475569]">
          Payload includes: event type, QR code ID, form title, session ID, responses, and timestamp. 
          All payloads are signed with an HMAC-SHA256 signature in the <code>X-Webhook-Signature</code> header.
        </p>
      </div>
    </div>
  );
};
